// KANBAN des User Stories, toutes missions — CCT-98.
//
// Colonnes = statuts réels du moteur (mission_userstory), pas une nomenclature d'affichage :
// le glisser-déposer POSE le statut, il ne fait pas qu'un tri visuel.
//
// Garde importante : le joueur ne peut poser que IN_PROGRESS / BLOCKED / TO_REVIEW. VALIDATED,
// REFUSED et REVIEWED sont l'affaire de l'inspection du moteur — le serveur les refuse (403),
// on n'ouvre donc pas ces colonnes au dépôt plutôt que de laisser l'utilisateur se heurter à
// une erreur après coup.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Image, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import ActionLoader from './ActionLoader';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import {
  abandonUs, listAllUserStories, setUsStatus, type BoardUserStory, type UsStatus,
} from '../api/userStories';
import { useNavigation } from '@react-navigation/native';
import { getScenarioForMission, loadRuntimeScenario } from '../data/scenarios/index';
import TaskDetailScreen from '../screens/TaskDetailScreen';
import UsDetail, { US_COLOR as COLUMN_COLOR, US_HANDS_OVER, US_ICON as COLUMN_ICON, US_LABEL as COLUMN_LABEL, CharacterAvatar } from './UsDetail';
import UsChatScreen from '../screens/UsChatScreen';
import CybDialog from './CybDialog';
import { useT } from '../i18n/useT';
import { appElementId, sameElement } from '../utils/elementId';
import { apiFetch } from '../api/client';

// BACKLOG et REVIEWED ne sont PAS des colonnes : une US en backlog n'est pas encore assignée
// au joueur (elle n'a rien à faire sur SON tableau), et REVIEWED est un état interne du moteur.
const COLUMNS: UsStatus[] = [
  'ASSIGNED', 'IN_PROGRESS', 'BLOCKED', 'TO_REVIEW', 'READY', 'VALIDATED', 'REFUSED',
];

// Libellés et couleurs viennent de UsDetail : une seule table pour tous les rendus d'US.

const isWeb = Platform.OS === 'web';

// React Native Web NE TRANSMET PAS `draggable` ni les événements de drag sur une `View` : le
// DOM sort sans attribut et le glisser-déposer ne démarre jamais. On enveloppe donc les zones
// de drag dans un vrai `div` (web uniquement) ; en natif, c'est un simple passe-plat.
const DragBox: React.FC<{ props?: any; style?: any; children: React.ReactNode }> = ({ props, style, children }) =>
  isWeb
    ? React.createElement('div', { ...(props ?? {}), style: { display: 'flex', flexDirection: 'column', ...(style ?? {}) } }, children)
    : <>{children}</>;

/** Avatar du personnage PROPRIÉTAIRE de l'US : c'est lui qui relance et qui tranche. */
const OwnerAvatar: React.FC<{ us: BoardUserStory; size?: number }> = ({ us, size = 20 }) => (
  <CharacterAvatar url={us.owner_avatarkey} name={us.owner_name} size={size} />
);

export default function UsKanban() {
  const t = useT();
  const navigation = useNavigation<any>();
  const { getAccessToken } = useAuth();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  const [items, setItems] = useState<BoardUserStory[]>([]);
  const [settable, setSettable] = useState<UsStatus[]>([]);
  const [missionFilter, setMissionFilter] = useState<number | null>(null);
  const [shownCols, setShownCols] = useState<UsStatus[]>(COLUMNS);
  // Le scénario n'est chargé que par l'écran de chapitre : sur le tableau de bord le store est
  // VIDE, et le volet restait sans contenu. On charge donc le blob de la mission sélectionnée.
  const [scenarioTick, setScenarioTick] = useState(0);
  const [selected, setSelected] = useState<BoardUserStory | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  //: Columns FOLDED by the user — CCT-395. Kept in memory, not persisted: this is a glance-time
  //: setting, and making it durable would have someone find a half-hidden board a week later with
  //: no memory of why.
  const [pliees, setPliees] = useState<string[]>([]);
  const plier = (col: string) => setPliees(cur => (
    cur.includes(col) ? cur.filter(c => c !== col) : [...cur, col]));
  const [overCol, setOverCol] = useState<UsStatus | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await listAllUserStories(getAccessToken());
      const list = res.userStories ?? [];
      setItems(list);
      setSettable(res.settable ?? []);
      // Le volet doit suivre la relecture : après un dépôt en « À relire », le moteur peut
      // avoir tranché (VALIDATED / REFUSED) et le volet affichait encore l'ancien statut.
      setSelected(cur => (cur ? list.find(u => u.id === cur.id) ?? null : null));
      setError('');
    } catch {
      setError(t('kanban.unavailable'));
    } finally { setBusy(false); }
  }, [getAccessToken]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!selected) return;
    const mid = selected.mission_id;
    void (async () => {
      await loadRuntimeScenario(mid, () =>
        apiFetch(`/me/stories/${mid}/scenario`, { token: getAccessToken(), auth: true }));
      setScenarioTick(t => t + 1);
    })().catch(() => {});
  }, [selected?.mission_id, getAccessToken]);

  const missions = useMemo(() => {
    const m = new Map<number, string>();
    items.filter(u => COLUMNS.includes(u.status))
      .forEach(u => m.set(u.mission_id, u.mission_title ?? `Mission ${u.mission_id}`));
    return [...m.entries()].map(([missionId, title]) => ({ missionId, title }));
  }, [items]);

  // Seules les US réellement ASSIGNÉES au joueur : le backlog appartient à la mission, pas à lui.
  const assigned = items.filter(u => COLUMNS.includes(u.status));
  const visible = missionFilter == null ? assigned : assigned.filter(u => u.mission_id === missionFilter);

  // CCT-345 — the task's conversation, opened OVER the board rather than by navigating away:
  // dropping a card is a board gesture, and reading an answer must not cost the overview. Same
  // choice as the task modal further down.
  const [chatFor, setChatFor] = useState<BoardUserStory | null>(null);

  const move = async (us: BoardUserStory, to: UsStatus) => {
    if (!(us.settable ?? settable).includes(to) || us.status === 'READY') return;
    if (us.status === to) return;
    const prev = items;
    // Optimiste : le kanban doit répondre au doigt, la relecture serveur corrigera.
    setItems(cur => cur.map(u => (u.id === us.id ? { ...u, status: to } : u)));
    setSelected(s => (s && s.id === us.id ? { ...s, status: to } : s));
    try {
      await setUsStatus(getAccessToken(), us.mission_id, us.element_id, to);
      // ⚠️ HANDING A TASK OVER IS ADDRESSING SOMEBODY (CCT-345). "To review" and "Blocked" are
      // messages to the owner character: the server has already written the player's own sentence
      // in the thread, so the board opens it too — the same gesture must not behave differently
      // here and on the task screen.
      if (US_HANDS_OVER.includes(to)) setChatFor({ ...us, status: to });
      // Le moteur réagit au changement (inspection, chat) et peut REPOSER un autre statut :
      // on relit plutôt que de faire confiance à l'optimiste.
      await load();
    } catch (e: any) {
      setItems(prev);
      setError(`Statut refusé : ${String(e?.message ?? e).slice(0, 120)}`);
    }
  };

  const canDrop = (col: UsStatus) => settable.includes(col);

  // Confirmation d'abandon : dialogue IN-APP (palette IDE), pas une alerte navigateur.
  const [abandonTarget, setAbandonTarget] = useState<BoardUserStory | null>(null);
  const [abandoning, setAbandoning] = useState(false);
  const doAbandon = async () => {
    if (!abandonTarget) return;
    setAbandoning(true);
    try {
      await abandonUs(getAccessToken(), abandonTarget.mission_id, abandonTarget.element_id);
      setAbandonTarget(null); await load();
    } catch (e: any) { setError(`Abandon refusé : ${String(e?.message ?? e).slice(0, 120)}`); }
    finally { setAbandoning(false); }
  };

  /** Contenu authoré de l'US (objectif, étapes, validation) — repris du scénario de la mission.
   *
   *  Deux identifiants pour la MÊME chose : le scénario porte `el-508`, le moteur `508`
   *  (mission_userstory.element_id). Comparer brut ne matche jamais — et en silence. */
  const usContent = useMemo(() => {
    if (!selected) return null;
    const sameId = sameElement;
    const sc: any = getScenarioForMission(selected.mission_id);
    for (const ch of sc?.chapters ?? []) {
      const item = (ch?.sequence ?? []).find((it: any) => sameId(it.id, selected.element_id));
      if (!item) continue;
      const c: any = item.content ?? {};
      return {
        objective: String(c.objective ?? ''),
        validation: String(c.validation ?? ''),
        // CodeController : le lien vers l'IDE n'a de sens QUE si une template est servie —
        // sinon « Ouvrir le projet » mène à une erreur (même garde que TaskDetail).
        codeTemplateId: c.controller === 'code' && c.projectTemplateId
          ? Number(c.projectTemplateId) : undefined,
        steps: (c.steps ?? []).map((st_: any, i: number) => ({
          n: Number(st_.stepNumber ?? i + 1),
          action: String(st_.action ?? ''),
          detail: String(st_.detail ?? ''),
        })),
        duration: item.duration,
      };
    }
    return null;
  }, [selected, scenarioTick]);

  const Card = (us: BoardUserStory) => {
    const dragProps = isWeb ? {
      draggable: true,
      onDragStart: (e: any) => { setDragId(us.id); e.dataTransfer?.setData('text/plain', String(us.id)); },
      onDragEnd: () => { setDragId(null); setOverCol(null); },
    } as any : {};
    return (
      <DragBox key={us.id} props={dragProps}>
        <TouchableOpacity
          onPress={() => setSelected(us)}
          activeOpacity={0.8}
          style={[st.card, {
            backgroundColor: colors.cardBackground,
            borderColor: selected?.id === us.id ? COLUMN_COLOR[us.status] : colors.border,
            opacity: dragId === us.id ? 0.5 : 1,
          }]}
        >
          <Text numberOfLines={2} style={[st.cardTitle, { color: colors.textPrimary }]}>
            {us.title || `US ${us.element_id}`}
          </Text>
          <View style={st.cardFoot}>
            <OwnerAvatar us={us} />
            <Text numberOfLines={1} style={[st.cardSub, { color: colors.textTertiary }]}>
              {us.mission_title ?? `Mission ${us.mission_id}`}
            </Text>
          </View>
          {!!us.controller && (
            <View style={[st.tag, { backgroundColor: COLUMN_COLOR[us.status] + '22' }]}>
              <Text style={[st.tagText, { color: COLUMN_COLOR[us.status] }]}>{us.controller}</Text>
            </View>
          )}
        </TouchableOpacity>
      </DragBox>
    );
  };

  const Column = (col: UsStatus) => {
    const list = visible.filter(u => u.status === col);
    const droppable = canDrop(col);
    const dropProps = isWeb ? {
      onDragOver: (e: any) => { if (droppable) { e.preventDefault(); setOverCol(col); } },
      onDragLeave: () => setOverCol(c => (c === col ? null : c)),
      onDrop: (e: any) => {
        e.preventDefault(); setOverCol(null);
        const us = items.find(u => u.id === dragId);
        if (us && droppable) void move(us, col);
        setDragId(null);
      },
    } as any : {};
    // ⚠️ THREE WIDTHS, ONE RULE: a column takes only what it has to show. Six columns at 260 px
    // overflowed every screen, and the empty ones — often half of them — took as much room as
    // those carrying the work.
    //
    // ⚠️ `undefined` RATHER THAN A NUMBER: the empty column then sizes itself on its header, that
    // is on its title. A small fixed value would truncate the longest label.
    const pliee = pliees.includes(col);
    const vide = list.length === 0;
    const largeur = pliee ? 46 : (vide ? undefined : 260);
    return (
      <DragBox key={col} props={dropProps} style={{ flex: 1, minHeight: 0 }}>
      <View style={[st.col, {
        width: largeur,
        backgroundColor: colors.surfaceSecondary,
        borderColor: overCol === col ? COLUMN_COLOR[col] : 'transparent',
      }]}>
        {/* The header FOLDS the column: the gesture sits where the title is read, not in a menu. */}
        <TouchableOpacity activeOpacity={0.7} onPress={() => plier(col)} style={st.colHead}
          accessibilityLabel={`${t(COLUMN_LABEL[col])} ${pliee ? 'deplier' : 'plier'}`}>
          {/* The column's icon, not a coloured dot: a dot only says "a status", and six of them
              side by side left the hue as the only difference — unreadable for whoever does not
              separate red from green, and unnameable for everyone else. */}
          <Ionicons name={COLUMN_ICON[col]} size={14} color={COLUMN_COLOR[col]} />
          {/* ⚠️ FOLDED, THE COLUMN KEEPS ITS COUNT. A strip without a number says nothing about
              what it hides: one would unfold it for nothing, or forget it entirely. */}
          {!pliee && (
            <Text style={[st.colTitle, { color: colors.textPrimary }]}>{t(COLUMN_LABEL[col])}</Text>
          )}
          <Text style={[st.colCount, { color: colors.textTertiary }]}>{list.length}</Text>
        </TouchableOpacity>
        {/* Une colonne non déposable est signalée AVANT le geste : ces statuts sont posés par
            le moteur, pas par le joueur. */}
        {!droppable && dragId != null && (
          <Text style={[st.locked, { color: colors.textTertiary }]}>
            <Ionicons name="lock-closed-outline" size={11} /> {t('kanban.decidedByEngine')}
          </Text>
        )}
        {!pliee && (
          <ScrollView style={{ flex: 1, minHeight: 0 }} contentContainerStyle={{ gap: 8 }}
            showsVerticalScrollIndicator={false}>
            {list.map(Card)}
            {/* ⚠️ NO MORE DASH IN AN EMPTY COLUMN: it now sizes itself on its title, and one more
                character teaches nothing the zero count does not already say. */}
          </ScrollView>
        )}
      </View>
      </DragBox>
    );
  };

  return (
    <View style={st.root}>
      <View style={st.head}>
        <Ionicons name="grid-outline" size={18} color={colors.primary} />
        <Text style={[st.title, { color: colors.textPrimary }]}>{t('kanban.title')}</Text>
        <Text style={[st.sub, { color: colors.textSecondary }]}>
          {visible.length} US{missions.length ? ` · ${missions.length} mission${missions.length > 1 ? 's' : ''}` : ''}
        </Text>
      </View>

      {missions.length > 1 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          style={st.chipsBar} contentContainerStyle={st.chips}>
          {[{ missionId: null as number | null, title: t('common.all') }, ...missions].map(o => {
            const on = missionFilter === o.missionId;
            return (
              <TouchableOpacity key={o.missionId ?? 'all'} onPress={() => setMissionFilter(o.missionId)}
                activeOpacity={0.75}
                style={[st.chip, {
                  backgroundColor: on ? colors.primary : colors.surfaceSecondary,
                  borderColor: on ? colors.primary : colors.border,
                }]}>
                <Text numberOfLines={1} style={[st.chipText, { color: on ? colors.badgeText : colors.textSecondary }]}>
                  {o.title}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}

      {/* Sélection des états affichés : un joueur qui suit son travail en cours n'a pas besoin
          de voir en permanence les colonnes terminées. */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        style={st.chipsBar} contentContainerStyle={st.chips}>
        <Text style={[st.chipsLabel, { color: colors.textTertiary }]}>{t('kanban.states')}</Text>
        {COLUMNS.map(c => {
          const on = shownCols.includes(c);
          return (
            <TouchableOpacity key={c} activeOpacity={0.75}
              onPress={() => setShownCols(cur => (
                // Au moins une colonne : un tableau vide n'apprend rien.
                on ? (cur.length > 1 ? cur.filter(x => x !== c) : cur) : [...cur, c]
              ))}
              // Même langage visuel que les statuts de l'US (CCT-161) : teinté toujours,
              // plus soutenu et cerclé quand le filtre est active.
              style={[st.chip, st.chipRow, {
                backgroundColor: COLUMN_COLOR[c] + (on ? '33' : '1A'),
                borderColor: on ? COLUMN_COLOR[c] : 'transparent',
                opacity: on ? 1 : 0.75,
              }]}>
              <Ionicons name={COLUMN_ICON[c]} size={12} color={COLUMN_COLOR[c]} />
              <Text numberOfLines={1} style={[st.chipText, { color: COLUMN_COLOR[c], fontWeight: on ? '700' : '600' }]}>
                {t(COLUMN_LABEL[c])}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {!!error && <Text style={[st.err, { color: colors.error }]}>{error}</Text>}

      {busy ? (
        <View style={st.center}><ActionLoader label={t('kanban.loading')} /></View>
      ) : (
        <View style={st.boardRow}>
          <ScrollView horizontal showsHorizontalScrollIndicator
            style={{ flex: 1 }} contentContainerStyle={st.board}>
            {COLUMNS.filter(c => shownCols.includes(c)).map(Column)}
          </ScrollView>

          {/* Volet de droite : le contenu de l'US sélectionnée. */}

        </View>
      )}

      {/* Abandon : énonce ce que ça COÛTE (tâche seule vs mission entière), d'après la config
          de l'auteur — une confirmation générique ne permet pas de décider. */}
      <CybDialog
        visible={!!abandonTarget}
        title={t('task.abandonConfirmTitle')}
        warning={abandonTarget?.abandon_effect === 'abandon_mission'}
        message={t(abandonTarget?.abandon_effect === 'abandon_mission'
          ? 'kanban.abandonNamedDecisive' : 'kanban.abandonNamedNormal',
          { title: abandonTarget?.title ?? '' })}
        confirmLabel={t('task.abandonConfirmLabel')}
        destructive
        busy={abandoning}
        onCancel={() => setAbandonTarget(null)}
        onConfirm={() => void doAbandon()}
      />
      {/* CCT-162 — la modale couvre le DASHBOARD, pas l'écran : la barre latérale doit rester
          visible et accessible. Placée à la racine du kanban (et non dans la zone du plateau),
          elle recouvre aussi les filtres, ce qui n'était pas le cas en la laissant plus bas. */}
      {selected && (
            // CCT-158 — MÊME rendu que dans la mission : on affiche l'écran d'US lui-même dans
            // une modale, plutôt qu'un volet au rendu voisin. Deux vues « presque pareilles »
            // divergeaient à chaque correctif (statut figé, intervenants, effort…).
            <TouchableOpacity activeOpacity={1} onPress={() => setSelected(null)}
              style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 100,
                backgroundColor: 'rgba(0,0,0,0.65)' }}>
              {/* PLEIN ÉCRAN (CCT-160) : l'US a deux colonnes et des blocs longs — dans une
                  boîte réduite, on passait son temps à défiler. Fermeture explicite en haut à
                  droite : le clic sur le fond ne se devine pas. */}
              <TouchableOpacity activeOpacity={1} onPress={() => {}}
                style={{ width: '100%', height: '100%', overflow: 'hidden', backgroundColor: colors.background }}>
                <TouchableOpacity onPress={() => setSelected(null)} accessibilityLabel={t('kanban.closeTask')}
                  style={{ position: 'absolute', top: 10, right: 12, zIndex: 10, padding: 8, borderRadius: 999,
                    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface }}>
                  <Ionicons name="close" size={20} color={colors.textPrimary} />
                </TouchableOpacity>
                <TaskDetailScreen
                  navigation={{
                    ...navigation,
                    goBack: () => setSelected(null),
                    // ⚠️ THE TASK SCREEN OPENS THE CONVERSATION BY NAVIGATING (CCT-345). Shown
                    // here inside the board's overlay, that navigation would push a screen
                    // UNDERNEATH this modal: the player would close the task and land on a chat
                    // they never asked for. Intercepted, it opens the board's own overlay.
                    navigate: (screen: string, params: any) => {
                      if (screen === 'UsChat') { setChatFor(selected); return; }
                      navigation.navigate(screen as never, params as never);
                    },
                  }}
                  route={{ params: { missionId: selected.mission_id, itemId: appElementId(selected.element_id),
                                     title: selected.title } }}
                  // ⚠️ LE PLATEAU NE SE RELISAIT QU'APRÈS UN GLISSER-DÉPOSER. Un statut changé
                  // DANS cette modale laissait la carte dans son ancienne colonne : le moteur
                  // partait bien, mais rien ne le montrait — d'où « le dashboard ne déclenche
                  // pas le moteur ». La relecture suit maintenant le geste, d'où qu'il vienne.
                  onStatusChanged={(statut) => {
                    setItems(cur => cur.map(u => (u.id === selected.id ? { ...u, status: statut } : u)));
                    setSelected(sel => (sel && sel.id === selected.id ? { ...sel, status: statut } : sel));
                    void load();
                  }}
                />
              </TouchableOpacity>
            </TouchableOpacity>
          )}

      {/* CCT-345 — THE CONVERSATION OF THE TASK JUST HANDED OVER. It opens on the player's own
          sentence, already written by the server; the owner character answers underneath, in the
          same thread. Above the task modal on purpose: handing over from inside the task must
          bring the conversation to the front, not hide it behind what one was reading. */}
      {chatFor && (
        <TouchableOpacity activeOpacity={1} onPress={() => setChatFor(null)}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 200,
            backgroundColor: 'rgba(0,0,0,0.65)' }}>
          <TouchableOpacity activeOpacity={1} onPress={() => {}}
            style={{ width: '100%', height: '100%', overflow: 'hidden', backgroundColor: colors.background }}>
            <TouchableOpacity onPress={() => setChatFor(null)} accessibilityLabel={t('kanban.closeTask')}
              style={{ position: 'absolute', top: 10, right: 12, zIndex: 10, padding: 8, borderRadius: 999,
                borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface }}>
              <Ionicons name="close" size={20} color={colors.textPrimary} />
            </TouchableOpacity>
            <UsChatScreen
              navigation={{ ...navigation, goBack: () => setChatFor(null) }}
              route={{ params: { missionId: chatFor.mission_id,
                                 elementId: appElementId(chatFor.element_id) } }}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      )}
    </View>
  );
}

const st = StyleSheet.create({
  root: { flex: 1, minHeight: 0, gap: 10, position: 'relative' },
  head: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  title: { fontSize: 16, fontWeight: '700' },
  sub: { fontSize: 12 },
  // Sans `flexGrow: 0`, une ScrollView prend le flex du parent en colonne : les bandeaux de
  // filtres s'étiraient sur toute la hauteur.
  chipsBar: { flexGrow: 0, flexShrink: 0 },
  chips: { gap: 8, alignItems: 'center', paddingVertical: 2, paddingRight: 4 },
  chipsLabel: { fontSize: 11, alignSelf: 'center', marginRight: 2 },
  chip: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 6, maxWidth: 220 },
  chipRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  chipText: { fontSize: 12, fontWeight: '600' },
  err: { fontSize: 12 },
  center: { paddingVertical: 32, alignItems: 'center' },

  // Le plateau occupe toute la hauteur restante : la barre de défilement horizontale et le
  // volet tombent ainsi en PIED DE PAGE, au lieu de flotter au milieu de l'écran.
  boardRow: { flex: 1, minHeight: 0, flexDirection: 'row', gap: 12 },
  board: { minHeight: 0, flexDirection: 'row', gap: 12, alignItems: 'stretch', justifyContent: 'flex-start' },
  // Largeur calculée pour que les SIX statuts tiennent à l'écran : au-delà, le plateau
  // défile horizontalement et on perd la vue d'ensemble, qui est tout l'intérêt d'un kanban.
  col: { flexGrow: 0, flexShrink: 0, borderRadius: 12, borderWidth: 2, padding: 10, gap: 8 },
  colHead: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  colTitle: { fontSize: 13, fontWeight: '700', flex: 1 },
  colCount: { fontSize: 12 },
  locked: { fontSize: 10, fontStyle: 'italic' },
  emptyCol: { fontSize: 12, textAlign: 'center', paddingVertical: 8 },

  card: { borderRadius: 10, borderWidth: 1, padding: 10, gap: 4 },
  cardTitle: { fontSize: 13, fontWeight: '600', lineHeight: 17 },
  cardSub: { fontSize: 11, flex: 1 },
  cardFoot: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  avatarFallback: { backgroundColor: '#475569', alignItems: 'center', justifyContent: 'center' },
  avatarText: { color: '#FFFFFF', fontWeight: '700' },
  tag: { alignSelf: 'flex-start', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2 },
  tagText: { fontSize: 10, fontWeight: '700' },

  panel: { width: 300, borderLeftWidth: 1, padding: 14, gap: 8, minHeight: 0 },
  panelHead: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  panelTitle: { fontSize: 15, fontWeight: '700', flex: 1 },
  close: { padding: 2 },
  badge: { alignSelf: 'flex-start', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4 },
  badgeText: { fontSize: 11, fontWeight: '700' },
  row: { fontSize: 12 },
  panelSection: { fontSize: 13, fontWeight: '700', marginTop: 8 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  action: { borderWidth: 1.5, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 7 },
  actionText: { fontSize: 12, fontWeight: '600' },
  ownerRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  panelBody: { flex: 1, marginTop: 6, minHeight: 0 },
  body: { fontSize: 12, lineHeight: 17 },
  bodyDim: { fontSize: 11, lineHeight: 16 },
  step: { flexDirection: 'row', gap: 6 },
  stepN: { fontSize: 12, fontWeight: '700' },
  ide: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
         borderRadius: 10, paddingVertical: 9, marginTop: 6 },
  ideText: { fontSize: 13, fontWeight: '700' },
});
